/**
 * API Error Class
 * For HTTP/fetch errors with status codes and request context
 * 
 * @module lib/errors/ApiError
 */

import { AppError, ErrorContext } from './AppError';

export interface ApiErrorContext extends ErrorContext {
    /** The URL that was requested */
    url?: string;
    /** HTTP method used */
    method?: string;
    /** Response body (if available) */
    responseBody?: unknown;
    /** Request headers (sanitized) */
    requestHeaders?: Record<string, string>;
}

export interface ApiErrorOptions {
    /** HTTP status code */
    statusCode: number;
    /** Error code (defaults based on status) */
    code?: string;
    /** Additional context */
    context?: ApiErrorContext;
    /** Original error */
    cause?: Error;
}

/**
 * Error class for API/HTTP errors
 * 
 * @example
 * ```typescript
 * throw new ApiError('Failed to fetch user', {
 *     statusCode: 404,
 *     context: { url: '/api/users/123', method: 'GET' }
 * });
 * ```
 */
export class ApiError extends AppError {
    constructor(message: string, options: ApiErrorOptions) {
        const code = options.code ?? ApiError.getCodeFromStatus(options.statusCode);

        super(message, {
            code,
            statusCode: options.statusCode,
            context: options.context ?? {},
            isOperational: true,
            cause: options.cause,
        });
    }

    /**
     * Get a standard error code based on HTTP status
     */
    static getCodeFromStatus(status: number): string {
        switch (status) {
            case 400: return 'BAD_REQUEST';
            case 401: return 'UNAUTHORIZED';
            case 403: return 'FORBIDDEN';
            case 404: return 'NOT_FOUND';
            case 408: return 'REQUEST_TIMEOUT';
            case 409: return 'CONFLICT';
            case 422: return 'UNPROCESSABLE_ENTITY';
            case 429: return 'TOO_MANY_REQUESTS';
            case 500: return 'INTERNAL_SERVER_ERROR';
            case 502: return 'BAD_GATEWAY';
            case 503: return 'SERVICE_UNAVAILABLE';
            case 504: return 'GATEWAY_TIMEOUT';
            default: return `HTTP_${status}`;
        }
    }

    /**
     * Create ApiError from a fetch Response
     */
    static async fromResponse(response: Response, context?: Partial<ApiErrorContext>): Promise<ApiError> {
        let responseBody: unknown;
        try {
            responseBody = await response.json();
        } catch {
            try {
                responseBody = await response.text();
            } catch {
                responseBody = null;
            }
        }

        const message = typeof responseBody === 'object' && responseBody !== null && 'message' in responseBody
            ? String((responseBody as { message: unknown }).message)
            : `Request failed with status ${response.status}`;

        return new ApiError(message, {
            statusCode: response.status,
            context: {
                url: response.url,
                responseBody,
                ...context,
            },
        });
    }

    /**
     * Check if error is a network/connection error
     */
    isNetworkError(): boolean {
        return this.statusCode === 0 || this.code === 'NETWORK_ERROR';
    }

    /**
     * Check if error is retryable
     */
    isRetryable(): boolean {
        const retryableStatuses = [408, 429, 500, 502, 503, 504];
        return retryableStatuses.includes(this.statusCode) || this.isNetworkError();
    }
}
